import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useTaskCollaborators } from '@/hooks/useTaskCollaborators';
import { useCompanyUsers } from '@/hooks/useCompanyUsers';
import { Plus, X } from 'lucide-react';
import type { CollaboratorRole } from '@/types/task';

interface TaskCollaboratorsProps {
  taskId: string;
}

const roleLabels: Record<CollaboratorRole, string> = {
  owner: 'Proprietário',
  assignee: 'Responsável',
  collaborator: 'Colaborador',
  watcher: 'Observador'
};

export const TaskCollaborators = ({ taskId }: TaskCollaboratorsProps) => {
  const { collaborators, loading, addCollaborator, removeCollaborator } = useTaskCollaborators(taskId);
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<CollaboratorRole>('collaborator');

  const collaboratorIds = collaborators.map(c => c.user_id);
  const { users } = useCompanyUsers(collaboratorIds);

  const handleAdd = async () => {
    if (!selectedUserId) return;
    
    try {
      await addCollaborator(selectedUserId, selectedRole);
      setOpen(false);
      setSelectedUserId('');
      setSelectedRole('collaborator');
    } catch (error) {
      // Error handled in hook
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Colaboradores</h4>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Colaborador</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Usuário</label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            avatarUrl={user.avatar_url}
                            name={user.full_name}
                            size="sm"
                          />
                          <span>{user.full_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Papel</label>
                <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as CollaboratorRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleAdd} disabled={!selectedUserId} className="w-full">
                Adicionar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Carregando...</div>
      ) : collaborators.length === 0 ? (
        <div className="text-sm text-muted-foreground">Nenhum colaborador</div>
      ) : (
        <div className="space-y-2">
          {collaborators.map(collab => (
            <div key={collab.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <UserAvatar
                  avatarUrl={collab.user?.avatar_url}
                  name={collab.user?.full_name}
                  size="sm"
                />
                <div>
                  <div className="text-sm font-medium">{collab.user?.full_name}</div>
                  <div className="text-xs text-muted-foreground">{roleLabels[collab.role]}</div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeCollaborator(collab.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
